import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Luggage, Trash2 } from "lucide-react";
import { api, money, toCriteria } from "../lib/api";
import { useBooking } from "../context/BookingContext";
import { SafeImg } from "./SafeImg";

export const useQuote = () => {
  const { criteria, hotel, room, rate, services } = useBooking();
  return useQuery({
    queryKey: ["quote", criteria, hotel?.id, room?.id, rate?.id, services],
    queryFn: () => api.post("/quote", { criteria: toCriteria(criteria), hotel_id: hotel.id, room_id: room.id, rate_id: rate.id, services }).then((r) => r.data),
    enabled: !!(hotel && room && rate),
    placeholderData: (prev) => prev,
  });
};

const Row = ({ label, value, testid, strong }) => (
  <div className={`flex justify-between gap-4 text-sm ${strong ? "font-medium text-ink" : "text-ink/70"}`}>
    <span>{label}</span><span className="font-mono" data-testid={testid}>{value}</span>
  </div>
);

const Stay = () => {
  const { criteria, departure } = useBooking();
  const d = (s) => format(parseISO(s), "d MMM yyyy");
  return (
    <div className="grid grid-cols-2 gap-3 rounded-xl bg-linen p-4 text-sm [&>*]:min-w-0" data-testid="itinerary-stay">
      <div><p className="eyebrow">Arrive</p><p className="mt-1 text-ink">{d(criteria.arrival)}</p></div>
      <div><p className="eyebrow">Depart</p><p className="mt-1 text-ink">{d(departure)}</p></div>
      <p className="col-span-2 text-ink/60">
        {criteria.nights} night{criteria.nights > 1 ? "s" : ""} · {criteria.rooms} room{criteria.rooms > 1 ? "s" : ""} · {criteria.adults} adult{criteria.adults > 1 ? "s" : ""}{criteria.kids ? ` · ${criteria.kids} kid${criteria.kids > 1 ? "s" : ""}` : ""} per room
      </p>
    </div>
  );
};

export const ItinerarySidebar = ({ children }) => {
  const { hotel, room, rate, clearItinerary } = useBooking();
  const { data: q } = useQuote();
  return (
    <aside className="rounded-2xl border border-brand/10 bg-white p-6 shadow-[0_20px_60px_-30px_rgba(0,92,77,0.35)] lg:self-start lg:sticky lg:top-28" data-testid="itinerary-sidebar">
      <div className="flex items-center justify-between">
        <h4 className="font-serif text-2xl text-ink">My Itinerary</h4>
        {hotel && <button onClick={clearItinerary} className="text-ink/40 hover:text-red-600" title="Clear itinerary" data-testid="itinerary-clear"><Trash2 className="h-4 w-4" /></button>}
      </div>
      <div className="mt-4 space-y-4">
        <Stay />
        {!hotel ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-ink/50" data-testid="itinerary-empty">
            <Luggage className="h-8 w-8 text-gold" strokeWidth={1.25} />
            There are currently no items in your itinerary
          </div>
        ) : (
          <div className="flex gap-3" data-testid="itinerary-hotel">
            <SafeImg src={hotel.image} alt={hotel.name} compact className="h-16 w-16 shrink-0 rounded-lg object-cover" />
            <div className="min-w-0">
              <p className="line-clamp-2 font-medium text-ink">{hotel.name}</p>
              <p className="text-xs text-ink/50">{hotel.city}</p>
              {room && <p className="mt-1 text-xs text-ink/70" data-testid="itinerary-room">{room.name} · {rate.name}</p>}
            </div>
          </div>
        )}
        {q && rate && (
          <div className="space-y-2 border-t border-ink/10 pt-4" data-testid="itinerary-totals">
            <Row label="Accommodation" value={money(q.room_total + q.discount, q.currency)} />
            {q.discount > 0 && <Row label={`Promo ${q.promo?.code}`} value={`−${money(q.discount, q.currency)}`} testid="itinerary-discount" />}
            {q.services.map((s) => <Row key={s.id} label={`${s.name} ×${s.qty}`} value={money(s.total, q.currency)} />)}
            <div className="border-t border-dashed border-ink/15 pt-2">
              <Row label="Total" value={money(q.total, q.currency)} testid="itinerary-total" strong />
            </div>
          </div>
        )}
        {children}
      </div>
    </aside>
  );
};
